import { useState, useEffect, useContext, useReducer } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  MessageCircle,
  Instagram,
  Facebook,
  Globe,
  ChevronDown,
  Bot,
  Users,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { parseISO, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

// Import from existing system
import useTickets from "../../../hooks/useTickets";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { i18n } from "../../../translate/i18n";

export type Conversation = {
  id: string;
  uuid: string;
  clientName: string;
  clientAvatar: string;
  lastMessage: string;
  timestamp: string;
  channel: "whatsapp" | "instagram" | "facebook" | "widget";
  unreadCount: number;
  isOnline: boolean;
  status: "open" | "pending" | "closed" | "bot" | "group";
  agentName: string;
  agentAvatar: string;
  priority: "high" | "medium" | "low";
  // Original ticket data for actions
  originalTicket?: any;
};

const channelIcons: Record<string, { icon: any; color: string }> = {
  whatsapp: { icon: MessageCircle, color: "#22C55E" },
  instagram: { icon: Instagram, color: "#E4405F" },
  facebook: { icon: Facebook, color: "#1877F2" },
  widget: { icon: Globe, color: "#2563EB" },
  webchat: { icon: Globe, color: "#2563EB" },
};

// Reducer for ticket management
const reducer = (state: any[], action: any) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;
    newTickets.forEach((ticket: any) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
      } else {
        state.push(ticket);
      }
    });
    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
    } else {
      state.unshift(ticket);
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

// Map ticket to conversation format
const mapTicketToConversation = (ticket: any): Conversation => {
  const channel = ticket.channel || "whatsapp";
  const validChannel = ["whatsapp", "instagram", "facebook", "widget", "webchat"].includes(channel)
    ? channel
    : "whatsapp";

  let timestamp = i18n.t("v2.ticket.now");
  if (ticket.updatedAt) {
    try {
      timestamp = formatDistanceToNow(parseISO(ticket.updatedAt), {
        addSuffix: false,
        locale: ptBR,
      });
    } catch {
      timestamp = i18n.t("v2.ticket.now");
    }
  }

  return {
    id: String(ticket.id),
    uuid: ticket.uuid || String(ticket.id),
    clientName: ticket.contact?.name || i18n.t("v2.ticket.noName"),
    clientAvatar: ticket.contact?.profilePicUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(ticket.contact?.name || "U")}&background=random`,
    lastMessage: ticket.lastMessage || "",
    timestamp,
    channel: validChannel as any,
    unreadCount: ticket.unreadMessages || 0,
    isOnline: true,
    status: ticket.status || "open",
    agentName: ticket.user?.name || i18n.t("v2.ticket.noAgent"),
    agentAvatar: "",
    priority: ticket.unreadMessages > 3 ? "high" : ticket.unreadMessages > 0 ? "medium" : "low",
    originalTicket: ticket,
  };
};

interface InboxListProps {
  selectedConversation: Conversation | null;
  onSelectConversation: (conversation: Conversation) => void;
}

export function InboxList({ selectedConversation, onSelectConversation }: InboxListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("open");
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const [tabCounts, setTabCounts] = useState({
    open: 0,
    pending: 0,
    closed: 0,
    bot: 0,
    groups: 0,
  });

  // Get auth context
  const { user, socket } = useContext(AuthContext) as any;
  const companyId = user?.companyId;

  // Get user queues
  const userQueues = user?.queues?.map((q: any) => q.id) || [];

  // Fetch tickets using the existing hook
  const { tickets, loading } = useTickets({
    pageNumber: 1,
    searchParam: searchQuery,
    status: activeTab === "groups" ? "group" : activeTab,
    showAll: user?.profile === "admin",
    queueIds: JSON.stringify(userQueues),
    withUnreadMessages: undefined,
    tags: JSON.stringify([]),
    users: JSON.stringify([]),
    date: undefined,
    updatedAt: undefined,
    whatsappIds: JSON.stringify([]),
    statusFilter: JSON.stringify([]),
    forceSearch: undefined,
    userFilter: undefined,
    sortTickets: "DESC",
    searchOnMessages: undefined,
  } as any);

  // Update tickets when fetched
  useEffect(() => {
    if (tickets && tickets.length > 0) {
      dispatch({ type: "LOAD_TICKETS", payload: tickets });
    }
  }, [tickets]);

  // Reset tickets when tab changes
  useEffect(() => {
    dispatch({ type: "RESET" });
  }, [activeTab, searchQuery]);

  // Socket listeners for real-time updates
  useEffect(() => {
    // Check if socket exists and has the 'on' method
    if (!socket || !companyId || typeof socket.on !== 'function') return;

    const onTicketUpdate = (data: any) => {
      if (data.action === "update" && data.ticket) {
        dispatch({ type: "UPDATE_TICKET", payload: data.ticket });
      }
      if (data.action === "delete" && data.ticketId) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    };

    const onAppMessage = (data: any) => {
      if (data.action === "create" && data.ticket) {
        dispatch({ type: "UPDATE_TICKET", payload: data.ticket });
      }
    };

    socket.on(`company-${companyId}-ticket`, onTicketUpdate);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      if (typeof socket.off === 'function') {
        socket.off(`company-${companyId}-ticket`, onTicketUpdate);
        socket.off(`company-${companyId}-appMessage`, onAppMessage);
      }
    };
  }, [socket, companyId]);

  // Convert tickets to conversations
  const conversations = ticketsList.map(mapTicketToConversation);

  // Determina o status esperado para a aba atual
  const expectedStatus = activeTab === "groups" ? "group" : activeTab;

  // Filter conversations por status da aba E por busca
  const filteredConversations = conversations.filter((conv) => {
    // Primeiro filtra pelo status da aba atual
    const matchesStatus = conv.status === expectedStatus;
    if (!matchesStatus) return false;

    // Depois filtra pela busca
    const matchesSearch =
      conv.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Update tab counts - conta de todos os tickets, não só os filtrados
  useEffect(() => {
    setTabCounts({
      open: conversations.filter((c: Conversation) => c.status === "open").length,
      pending: conversations.filter((c: Conversation) => c.status === "pending").length,
      closed: conversations.filter((c: Conversation) => c.status === "closed").length,
      bot: conversations.filter((c: Conversation) => c.status === "bot").length,
      groups: conversations.filter((c: Conversation) => c.status === "group").length,
    });
  }, [conversations]);

  const tabs = [
    { id: "open", label: i18n.t("v2.inbox.tabs.open"), count: tabCounts.open },
    { id: "pending", label: i18n.t("v2.inbox.tabs.pending"), count: tabCounts.pending },
    { id: "closed", label: i18n.t("v2.inbox.tabs.closed"), count: tabCounts.closed },
    { id: "bot", label: "Bot", count: tabCounts.bot },
    { id: "groups", label: i18n.t("v2.inbox.tabs.groups"), count: tabCounts.groups },
  ];

  return (
    <div className="flex h-full w-full flex-col border-r border-[var(--border-color)] bg-[var(--sidebar-bg)] transition-colors duration-300">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[var(--border-color)] p-3 sm:p-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder={i18n.t("v2.inbox.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 sm:h-10 w-full rounded-xl border border-[var(--border-color)] bg-[var(--input-bg)] pl-9 sm:pl-10 pr-9 sm:pr-10 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] transition-all focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20"
          />
          <button className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-secondary)]">
            <Filter className="h-4 w-4" />
          </button>
        </div>

        {/* Queue Selector */}
        <div className="mt-2 sm:mt-3 flex items-center gap-2">
          <button className="flex flex-1 items-center justify-between rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors">
            <span>{i18n.t("v2.inbox.allQueues")}</span>
            <ChevronDown className="h-4 w-4 text-[var(--text-secondary)]" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 border-b border-[var(--border-color)] px-1.5 sm:px-2">
        <div className="flex gap-0.5 sm:gap-1 overflow-x-auto py-1.5 sm:py-2 scrollbar-none">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-1 sm:gap-1.5 whitespace-nowrap rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium transition-all",
                activeTab === tab.id
                  ? "bg-[#2563EB] text-white"
                  : "text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
              )}
            >
              {tab.id === "bot" && <Bot className="h-3 w-3" />}
              {tab.id === "groups" && <Users className="h-3 w-3" />}
              <span>{tab.label}</span>
              <span
                className={cn(
                  "flex h-4 sm:h-5 min-w-[16px] sm:min-w-[20px] items-center justify-center rounded-full px-1 text-[10px] sm:text-xs font-semibold",
                  activeTab === tab.id
                    ? "bg-white/20 text-white"
                    : "bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                )}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto p-[2px] space-y-[2px]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#2563EB] border-t-transparent" />
          </div>
        ) : (
          <AnimatePresence>
            {filteredConversations.map((conversation, index) => {
              const channelData = channelIcons[conversation.channel] || channelIcons.whatsapp;
              const ChannelIcon = channelData.icon;
              const isSelected = selectedConversation?.id === conversation.id;

              return (
                <motion.div
                  key={conversation.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => onSelectConversation(conversation)}
                  className={cn(
                    "group relative cursor-pointer rounded-lg p-3 sm:p-4 transition-all",
                    isSelected
                      ? "bg-[#2563EB]/10 dark:bg-[#2563EB]/20 border-l-4 border-l-[#2563EB] shadow-sm"
                      : "hover:bg-[var(--bg-tertiary)] border-l-4 border-l-transparent"
                  )}
                >
                  {/* Priority Indicator */}
                  {conversation.priority === "high" && (
                    <div className="absolute right-3 sm:right-4 top-3 sm:top-4 h-2 w-2 rounded-full bg-[#EF4444]" />
                  )}

                  <div className="flex gap-2.5 sm:gap-3">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <img
                        src={conversation.clientAvatar}
                        alt={conversation.clientName}
                        className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.clientName)}&background=random`;
                        }}
                      />
                      {conversation.isOnline && (
                        <span className="absolute bottom-0 right-0 h-3 w-3 sm:h-3.5 sm:w-3.5 rounded-full border-2 border-[var(--sidebar-bg)] bg-[#22C55E]" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="truncate text-xs sm:text-sm font-semibold text-[var(--text-primary)]">
                          {conversation.clientName}
                        </h4>
                        <span className="flex-shrink-0 text-[10px] sm:text-xs text-[var(--text-muted)]">
                          {conversation.timestamp}
                        </span>
                      </div>

                      <p className="mt-0.5 sm:mt-1 line-clamp-2 text-[11px] sm:text-xs text-[var(--text-secondary)] leading-relaxed">
                        {conversation.lastMessage || i18n.t("v2.ticket.noMessages")}
                      </p>

                      <div className="mt-1.5 sm:mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <ChannelIcon
                            className="h-3 w-3 sm:h-3.5 sm:w-3.5"
                            style={{ color: channelData.color }}
                          />
                          <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">
                            {conversation.agentName.split(" ")[0]}
                          </span>
                        </div>

                        {conversation.unreadCount > 0 && (
                          <span className="flex h-4 sm:h-5 min-w-[16px] sm:min-w-[20px] items-center justify-center rounded-full bg-[#2563EB] px-1 sm:px-1.5 text-[10px] sm:text-xs font-bold text-white">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}

        {!loading && filteredConversations.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--bg-tertiary)]">
              <MessageCircle className="h-7 w-7 text-[var(--text-muted)]" />
            </div>
            <p className="mt-4 text-sm font-medium text-[var(--text-secondary)]">
              {i18n.t("v2.inbox.noConversations")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
